import { isAfter, subDays } from 'date-fns';

import { STORAGE_KEYS } from '@/constants/storageKeys';
import { ONE_WEEK_IN_DAYS } from '@/constants/time';
import { getPlatformStorage, safeStorageOp } from '@/util/platformStorage';
import { SURVEY_CONSENT_FIELD_PREFIX } from '../model/surveySchema';

/**
 * Answers as the form holds them mid-completion: a string per free-text and
 * single-choice question, a string array per checkbox question.
 */
export type SurveyDraftValues = Record<string, string | string[]>;

type StoredSurveyDraft = {
  surveyDefinitionId: string;
  savedAt: string;
  values: SurveyDraftValues;
  /** The page the respondent had reached, so a resumed survey reopens where they left. */
  pageName?: string;
};

/** A restored draft: the answers, plus where in the survey they were given. */
export type RestoredSurveyDraft = {
  values: SurveyDraftValues;
  pageName?: string;
};

/**
 * One key per survey rather than a single map of every draft, unlike visits.
 * A draft is only ever read for the survey being opened, so nothing here has
 * to enumerate them, and keeping them apart stops one long free-text answer
 * from being rewritten on every keystroke in an unrelated survey.
 *
 * Guests share one scope. They have no identity to key on, and the public
 * screen collects the respondent's email at submit time rather than up front,
 * so there is nothing earlier to partition by.
 */
function draftStorageKey(userId: string | undefined, surveyGroupId: string): string {
  return `${STORAGE_KEYS.SURVEY_DRAFT_PREFIX}:${userId ?? 'guest'}:${surveyGroupId}`;
}

function isDraftUnexpired(savedAt: string): boolean {
  return isAfter(new Date(savedAt), subDays(new Date(), ONE_WEEK_IN_DAYS));
}

/**
 * Keeps only values shaped the way a question renderer can consume them.
 *
 * Storage is user-writable and survives a survey being re-authored, so a value
 * can come back the wrong shape for the question now holding that name — a
 * string where the checkbox renderer will map over an array. Restoring it
 * would crash the form on the very screen meant to recover the user's work, so
 * an unusable value is dropped and its question simply starts blank.
 */
function sanitizeValues(values: unknown): SurveyDraftValues {
  if (!values || typeof values !== 'object' || Array.isArray(values)) {
    return {};
  }

  const sanitized: SurveyDraftValues = {};

  for (const [name, value] of Object.entries(values)) {
    if (typeof value === 'string') {
      sanitized[name] = value;
    } else if (Array.isArray(value) && value.every((entry) => typeof entry === 'string')) {
      sanitized[name] = value as string[];
    }
  }

  return sanitized;
}

function isAnswered(value: string | string[]): boolean {
  return Array.isArray(value) ? value.length > 0 : value.trim().length > 0;
}

/**
 * Drops the acknowledgement fields that ride alongside a Primum inquiry
 * question, and any question left blank.
 *
 * An attestation is deliberately never restored. It is the respondent's own
 * statement about the answer they are submitting now, so re-presenting it
 * pre-ticked would attest on their behalf for text they have not re-read —
 * the one thing the checkbox exists to prevent. Retyping is not at stake
 * either way, since the box is a single tap.
 *
 * Blank answers are dropped so that a draft holding nothing is indistinguish-
 * able from no draft at all, which is what lets a survey the user opened and
 * closed without typing avoid leaving a stored draft behind.
 */
export function toStorableDraftValues(values: Record<string, unknown>): SurveyDraftValues {
  return Object.fromEntries(
    Object.entries(sanitizeValues(values)).filter(
      ([name, value]) => !name.startsWith(SURVEY_CONSENT_FIELD_PREFIX) && isAnswered(value)
    )
  );
}

/**
 * Narrows a restored draft to the answers the form in front of the user can
 * actually hold: a question it still renders, whose answer is still the same
 * kind of value.
 *
 * The definition-id check on read already rejects a draft from a different
 * published version, so this guards the rarer case of storage that was edited
 * or corrupted directly. It matters because a value surviving this filter is
 * fed straight into form state and, from there, into a submitted response —
 * an unknown key would be sent as an answer to a question that does not
 * exist.
 */
export function selectRestorableValues(
  values: SurveyDraftValues,
  defaultValues: Record<string, unknown>
): SurveyDraftValues {
  return Object.fromEntries(
    Object.entries(values).filter(
      ([name, value]) =>
        name in defaultValues && Array.isArray(value) === Array.isArray(defaultValues[name])
    )
  );
}

/**
 * Reads back the partial answers saved for a survey, or `null` when there is
 * nothing usable to restore.
 *
 * The draft is keyed by survey *group* id, because that is the stable id a
 * user navigates back to, but it records the *definition* id it was typed
 * against and is discarded when those disagree. A republished survey can
 * rename, retype, or drop a question while keeping the group id, so answers
 * from the previous version would land in fields that no longer mean what
 * they meant when the user wrote them.
 *
 * Known limitation, shared with survey visits: drafts are device-local, so
 * answers started on mobile are not offered back on web.
 */
export async function readSurveyDraft(
  userId: string | undefined,
  surveyGroupId: string,
  surveyDefinitionId: string
): Promise<RestoredSurveyDraft | null> {
  const raw = await safeStorageOp(
    () => getPlatformStorage(userId, 'local').getItem(draftStorageKey(userId, surveyGroupId)),
    { component: 'surveyDrafts', action: 'read' }
  );

  if (!raw) {
    return null;
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  const draft = parsed as Partial<StoredSurveyDraft>;

  if (draft.surveyDefinitionId !== surveyDefinitionId) {
    return null;
  }

  if (typeof draft.savedAt !== 'string' || !isDraftUnexpired(draft.savedAt)) {
    return null;
  }

  const values = toStorableDraftValues(draft.values ?? {});

  if (Object.keys(values).length === 0) {
    return null;
  }

  return {
    values,
    pageName: typeof draft.pageName === 'string' ? draft.pageName : undefined,
  };
}

/**
 * Saves the answers filled in so far, or removes the draft when nothing
 * answerable is left — clearing the last field a user typed should leave no
 * draft rather than an empty one that still counts as work in progress.
 */
export async function writeSurveyDraft(
  userId: string | undefined,
  surveyGroupId: string,
  surveyDefinitionId: string,
  values: Record<string, unknown>,
  pageName?: string
): Promise<void> {
  const storableValues = toStorableDraftValues(values);

  if (Object.keys(storableValues).length === 0) {
    await clearSurveyDraft(userId, surveyGroupId);

    return;
  }

  const draft: StoredSurveyDraft = {
    surveyDefinitionId,
    savedAt: new Date().toISOString(),
    values: storableValues,
    pageName,
  };

  await safeStorageOp(
    () =>
      getPlatformStorage(userId, 'local').setItem(
        draftStorageKey(userId, surveyGroupId),
        JSON.stringify(draft)
      ),
    { component: 'surveyDrafts', action: 'write' }
  );
}

/**
 * Discards a survey's saved answers, once they have been submitted or the
 * user has otherwise finished with them.
 */
export async function clearSurveyDraft(
  userId: string | undefined,
  surveyGroupId: string
): Promise<void> {
  await safeStorageOp(
    () => getPlatformStorage(userId, 'local').removeItem(draftStorageKey(userId, surveyGroupId)),
    { component: 'surveyDrafts', action: 'clear' }
  );
}
